import { useEffect, useRef, useState } from 'react';
import type { PairedPhone } from '../phone-sync.js';
import { busyToasts, phoneChanges, RecentToasts, type PhoneToast } from '../phone-toasts.js';

const SHOW_MS = 4000;

/**
 * Floating notices over the map for what the paired phones are doing (#35):
 * work in progress stays until it ends, finished changes fade after a few seconds.
 */
export function PhoneToasts({ phones, tripName }: {
  phones: PairedPhone[] | undefined;
  tripName: (tripId: string) => string;
}) {
  const [recent, setRecent] = useState<PhoneToast[]>([]);
  const previous = useRef<PairedPhone[] | undefined>(undefined);
  const board = useRef<RecentToasts | null>(null);
  const names = useRef(tripName);
  useEffect(() => { names.current = tripName; }, [tripName]);

  // Declared before the effect that feeds it. Dismissal timers live as long as the component, not as long as a snapshot.
  useEffect(() => {
    const created = new RecentToasts(setRecent, SHOW_MS);
    board.current = created;
    return () => { created.dispose(); board.current = null; };
  }, []);

  useEffect(() => {
    if (!phones) return;
    const changes = phoneChanges(previous.current, phones, (id) => names.current(id));
    previous.current = phones;
    board.current?.add(changes);
  }, [phones]);

  const toasts = [...busyToasts(phones, tripName), ...recent];
  if (toasts.length === 0) return null;
  return (
    <div className="toast-stack" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <p key={toast.id} className={`toast toast-${toast.tone}`}>
          <span className={toast.tone === 'busy' ? 'phone-dot phone-dot-live phone-busy' : toast.tone === 'done' ? 'phone-dot phone-dot-live' : 'phone-dot'} aria-hidden="true" />
          {toast.text}
        </p>
      ))}
    </div>
  );
}
